import { useRef, createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";

const InventoryContext = createContext();

export const InventoryProvider = ({ children }) => {
  const { user } = useAuth();

  const [inventory, setInventory] = useState([]);
  const [valuationMethod, setValuationMethod] = useState("FIFO");
  const [batches, setBatches] = useState([]);

  const isSavingRef = useRef(false);

  const fetchItems = async () => {
    try {
      const response = await fetch("/api/inventory/items", {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to fetch inventory items");
      }

      const data = await response.json();

      if (data.status === "success") {
        setInventory(data.data || []);
      }
    } catch (error) {
      console.error("Fetch Items Error:", error);
    }
  };

  const fetchBatches = async () => {
    try {
      const response = await fetch("/api/inventory/batches", {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to fetch inventory batches");
      }

      const data = await response.json();

      if (data.status === "success") {
        setBatches(data.data || []);
      }
    } catch (error) {
      console.error("Fetch Batches Error:", error);
    }
  };

  useEffect(() => {
    if (user) {
      fetchItems();
      fetchBatches();
    }
  }, [user]);

  const addNewItem = async (item) => {
    if (isSavingRef.current) {
      return {
        status: "error",
        message: "Please wait. The item is already being saved.",
      };
    }

    isSavingRef.current = true;

    try {
      const response = await fetch("/api/inventory/items", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          name: item.name,
          department_id: Number(item.department_id),
          category: item.category,
          type: item.type,
          minStock: Number(item.minStock || 0),
          branch_id: Number(item.branch_id),
        }),
      });

      const data = await response.json();

      if (data.status !== "success") {
        return data;
      }

      const itemId = data.id;

      if (Number(item.stock) > 0) {
        const inwardResponse = await fetch(
          "/api/inventory/inward",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
              entries: [
                {
                  item_id: itemId,
                  qty: Number(item.stock),
                  unit: item.unit || "Kg",
                  branch_id: Number(item.branch_id),
                  source_type: "opening",
                  reference: "opening stock",
                },
              ],
            }),
          },
        );

        const inwardData = await inwardResponse.json();

        if (inwardData.status !== "success") {
          return {
            status: "error",
            message:
              inwardData.message ||
              "Item was created, but opening stock could not be added.",
          };
        }
      }

      await fetchItems();
      await fetchBatches();

      return {
        status: "success",
        id: itemId,
      };
    } catch (error) {
      console.error("Add Item Error:", error);

      return {
        status: "error",
        message: "Could not save inventory item.",
      };
    } finally {
      isSavingRef.current = false;
    }
  };

  const getConsumableBatches = (itemName) => {
    const item = inventory.find((inventoryItem) => inventoryItem.name === itemName);

    if (!item) return [];

    const itemBatches = batches.filter((batch) => batch.item_id === item.id);

    if (valuationMethod === "FIFO") {
      itemBatches.sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      );
    } else if (valuationMethod === "LIFO") {
      itemBatches.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      );
    }

    return itemBatches;
  };

  return (
    <InventoryContext.Provider
      value={{
        inventory,
        addNewItem,
        fetchItems,
        valuationMethod,
        setValuationMethod,
        getConsumableBatches,
      }}
    >
      {children}
    </InventoryContext.Provider>
  );
};

export const useInventory = () => useContext(InventoryContext);